/**
 * HistoryPage Component
 * Dedicated page for viewing and managing invoice history
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useHistoryStore } from '@/stores/historyStore';
import { useInvoiceStore } from '@/stores/invoiceStore';
import { formatDate } from '@/utils/date';
import type { Invoice } from '@shared/schemas/invoice.schema';

// Helper function to calculate invoice total
function calculateInvoiceTotal(invoice: Invoice): number {
  const subtotal = invoice.subtotal;
  const discountAmount = (subtotal * invoice.discountRate) / 100;
  const shipping = invoice.shipping || 0;
  const taxAmount = ((subtotal - discountAmount) * invoice.taxRate) / 100;
  return subtotal - discountAmount + shipping + taxAmount;
}

export function HistoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { history, removeFromHistory, clearHistory } = useHistoryStore();
  const { setCurrentInvoice } = useInvoiceStore();

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'amount' | 'number'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Filter and sort history
  const filteredHistory = useMemo(() => {
    let filtered = [...history];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((item) => {
        const { invoice } = item;
        const total = calculateInvoiceTotal(invoice);
        return (
          (invoice.invoiceNumber && invoice.invoiceNumber.toLowerCase().includes(query)) ||
          invoice.fromCompany?.toLowerCase().includes(query) ||
          invoice.toCompany?.toLowerCase().includes(query) ||
          total.toString().includes(query)
        );
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'date':
          comparison = new Date(a.savedAt).getTime() - new Date(b.savedAt).getTime();
          break;
        case 'amount':
          comparison = calculateInvoiceTotal(a.invoice) - calculateInvoiceTotal(b.invoice);
          break;
        case 'number':
          const aNum = a.invoice.invoiceNumber || '';
          const bNum = b.invoice.invoiceNumber || '';
          comparison = aNum.localeCompare(bNum);
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [history, searchQuery, sortBy, sortOrder]);

  const handleLoadInvoice = (invoice: Invoice) => {
    setCurrentInvoice(invoice);
    navigate({ to: '/' });
  };

  const handleRemoveInvoice = (invoiceId: string) => {
    if (confirm(t('history.confirmRemove'))) {
      removeFromHistory(invoiceId);
    }
  };

  const handleClearHistory = () => {
    if (confirm(t('history.clearConfirm'))) {
      clearHistory();
    }
  };

  const handleSort = (column: 'date' | 'amount' | 'number') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate({ to: '/' })}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label={t('common.back')}
              >
                <svg
                  className="w-6 h-6 text-gray-700 dark:text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {t('history.pageTitle')}
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('history.pageSubtitle', { count: history.length })}
                </p>
              </div>
            </div>
            {history.length > 0 && (
              <button
                onClick={handleClearHistory}
                className="px-4 py-2 text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              >
                {t('history.clearAll')}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full px-4 sm:px-6 lg:px-8 py-6">
        {/* Search and Filter Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1">
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('history.searchPlaceholder')}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            {/* Sort Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => handleSort('date')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  sortBy === 'date'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {t('history.sortByDate')}
                {sortBy === 'date' && (
                  <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                )}
              </button>
              <button
                onClick={() => handleSort('amount')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  sortBy === 'amount'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {t('history.sortByAmount')}
                {sortBy === 'amount' && (
                  <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                )}
              </button>
              <button
                onClick={() => handleSort('number')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  sortBy === 'number'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {t('history.sortByNumber')}
                {sortBy === 'number' && (
                  <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Results Info */}
        {searchQuery && (
          <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            {t('history.resultsCount', {
              count: filteredHistory.length,
              total: history.length,
            })}
          </div>
        )}

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {filteredHistory.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <svg
                className="w-16 h-16 mx-auto mb-4 text-gray-400 dark:text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                {searchQuery ? t('history.noSearchResults') : t('history.empty')}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {searchQuery
                  ? t('history.tryDifferentSearch')
                  : t('history.emptyDescription')}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => navigate({ to: '/' })}
                  className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                >
                  {t('history.createFirstInvoice')}
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Table Header - Desktop */}
              <div className="hidden md:block">
                <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <div className="col-span-3 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    {t('history.invoiceNumber')}
                  </div>
                  <div className="col-span-3 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    {t('history.companies')}
                  </div>
                  <div className="col-span-2 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    {t('history.date')}
                  </div>
                  <div className="col-span-2 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider text-right">
                    {t('history.amount')}
                  </div>
                  <div className="col-span-2 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider text-right">
                    {t('history.actions')}
                  </div>
                </div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredHistory.map((item) => (
                  <div
                    key={item.invoice.id}
                    className="md:grid md:grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    {/* Mobile Card Header / Desktop Invoice Number */}
                    <div className="col-span-3">
                      <div className="md:hidden">
                        <div className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                          {item.invoice.invoiceNumber || formatDate(typeof item.invoice.invoiceDate === 'string' ? item.invoice.invoiceDate : new Date(item.invoice.invoiceDate).toISOString()) || t('history.unnumbered')}
                        </div>
                      </div>
                      <div className="hidden md:block text-sm font-medium text-gray-900 dark:text-gray-100">
                        {item.invoice.invoiceNumber || formatDate(typeof item.invoice.invoiceDate === 'string' ? item.invoice.invoiceDate : new Date(item.invoice.invoiceDate).toISOString()) || t('history.unnumbered')}
                      </div>
                    </div>

                    {/* Companies */}
                    <div className="col-span-3">
                      <div className="md:hidden mb-2">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                          {t('history.companies')}
                        </div>
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          {item.invoice.fromCompany} → {item.invoice.toCompany}
                        </div>
                      </div>
                      <div className="hidden md:block text-sm text-gray-600 dark:text-gray-400">
                        {item.invoice.fromCompany} → {item.invoice.toCompany}
                      </div>
                    </div>

                    {/* Date */}
                    <div className="col-span-2">
                      <div className="md:hidden mb-2">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                          {t('history.date')}
                        </div>
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          {formatDate(item.savedAt)}
                        </div>
                      </div>
                      <div className="hidden md:block text-sm text-gray-600 dark:text-gray-400">
                        {formatDate(item.savedAt)}
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="col-span-2">
                      <div className="md:hidden mb-3">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                          {t('history.amount')}
                        </div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {item.invoice.currency}
                          {calculateInvoiceTotal(item.invoice).toFixed(2)}
                        </div>
                      </div>
                      <div className="hidden md:block text-sm text-right font-semibold text-gray-900 dark:text-gray-100">
                        {item.invoice.currency}
                        {calculateInvoiceTotal(item.invoice).toFixed(2)}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="col-span-2">
                      <div className="md:hidden">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleLoadInvoice(item.invoice)}
                            className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
                          >
                            {t('history.load')}
                          </button>
                          <button
                            onClick={() => handleRemoveInvoice(item.invoice.id!)}
                            className="px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            aria-label={t('history.remove')}
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="hidden md:flex justify-end gap-2">
                        <button
                          onClick={() => handleLoadInvoice(item.invoice)}
                          className="px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                        >
                          {t('history.load')}
                        </button>
                        <button
                          onClick={() => handleRemoveInvoice(item.invoice.id!)}
                          className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          aria-label={t('history.remove')}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer with count */}
              <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('history.showing', {
                    current: filteredHistory.length,
                    total: history.length,
                  })}
                </p>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
